import {
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';

import {
  SOCIAL_STORIES_BOT_URL,
  SOCIAL_STORIES_PDF_URL,
} from '@/constants/social-stories';

const pages = [
  {
    label: 'Cover',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/19964df9-59ea-43b9-bb3f-784faab33dac.png',
    text: "David's First Lunch in the Cafeteria",
    isTitle: true,
  },
  {
    label: 'Page 1',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/5670f8c7-2485-4de3-8156-7d4b9e91b914.png',
    text: 'Today is a special day! David is going to the school cafeteria for lunch for the first time.',
  },
  {
    label: 'Page 2',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/fd5842a8-5608-44d9-aa2a-b26216eca2cf.png',
    text: 'When it is lunchtime, David walks in a quiet line with his teacher and friends.',
  },
  {
    label: 'Page 3',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/858f5f6e-122b-405a-bfc5-e7ec902897e4.png',
    text: 'The cafeteria is a very big room with many tables and chairs. It can be a little bit noisy.',
  },
  {
    label: 'Page 4',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c71d73cf-da17-4614-a5af-947389c3b62a.png',
    text: 'If the noise feels too loud, David can wear his special headphones to help his ears feel better.',
  },
  {
    label: 'Page 5',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/066cec06-049d-46e3-afbe-14040207b16f.png',
    text: 'David stays in the line to get his lunch tray. He waits patiently for his turn.',
  },
  {
    label: 'Page 6',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/fcb3309e-a866-48a3-9c4d-8c69b4ee12ea.png',
    text: 'If David feels worried, he can take three slow, deep breaths to help his body feel calm.',
  },
  {
    label: 'Page 7',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/949ccafb-1cff-4714-9a41-8e84fdd38404.png',
    text: 'David finds a seat at the table. He sits with his friends and looks at all the yummy food.',
  },
  {
    label: 'Page 8',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/903b962e-7c70-4ce9-90c0-2d5cd70f0eb5.png',
    text: 'David eats his lunch and talks quietly with his friends. Eating lunch at school is fun!',
  },
  {
    label: 'Page 9',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/d4a16049-4026-4a47-9fbb-0ada960c4140.png',
    text: 'When David is finished, he carefully carries his tray to the bin. He is a great helper.',
  },
  {
    label: 'Page 10',
    image:
      'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/b7d49656-1938-4c42-b8dc-cddde450f3ba.png',
    text: 'David did it! He had a wonderful first lunch in the cafeteria. David is very proud of himself.',
  },
];

const SocialStoryExample = () => {
  const [currentPage, setCurrentPage] = useState(0);

  const goToPrev = () => setCurrentPage((p) => Math.max(0, p - 1));
  const goToNext = () =>
    setCurrentPage((p) => Math.min(pages.length - 1, p + 1));

  return (
    <section className='bg-white dark:bg-slate-900'>
      <div className='mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8'>
        {/* Header */}
        <div className='mx-auto max-w-3xl text-center'>
          <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            See a Real AI-Generated Social Story
          </h2>
          <p className='mt-4 text-lg text-gray-600 dark:text-slate-400'>
            This illustrated story was created by our AI for David, a
            kindergartener with autism who is going to the school cafeteria for
            the first time. Each page features a custom illustration and
            age-appropriate text.
          </p>
        </div>

        {/* Story details badge */}
        <div className='mt-8 flex flex-wrap justify-center gap-3'>
          <span className='inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'>
            Kindergarten (5-6)
          </span>
          <span className='inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300'>
            School Cafeteria
          </span>
          <span className='inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'>
            Autism / Sensory Sensitivity
          </span>
        </div>

        {/* Book viewer */}
        <div className='mt-12'>
          <div className='mx-auto max-w-3xl'>
            {/* All pages rendered for SEO, only active one visible */}
            {pages.map((p, i) => (
              <div
                key={p.label}
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-b from-amber-50 to-orange-50 shadow-xl dark:from-slate-800 dark:to-slate-800 ${
                  i === currentPage ? '' : 'sr-only'
                }`}
                aria-hidden={i !== currentPage}
              >
                {/* Image */}
                <div
                  className={`relative w-full ${p.isTitle ? 'aspect-square' : 'aspect-video'}`}
                >
                  <Image
                    src={p.image}
                    alt={p.text}
                    fill
                    className='object-cover'
                    sizes='(max-width: 640px) 100vw, 672px'
                    priority={i === 0}
                    loading={i === 0 ? 'eager' : 'lazy'}
                  />
                </div>

                {/* Text overlay */}
                <div className='bg-white px-6 py-5 dark:bg-slate-800'>
                  <p
                    className={`text-center text-gray-800 dark:text-slate-200 ${
                      p.isTitle
                        ? 'text-xl font-bold sm:text-2xl'
                        : 'text-base sm:text-lg'
                    }`}
                  >
                    {p.text}
                  </p>
                </div>

                {/* Page label */}
                <div className='absolute left-4 top-4'>
                  <span className='rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm dark:bg-slate-700/90 dark:text-slate-300'>
                    {p.label}
                  </span>
                </div>
              </div>
            ))}

            {/* Navigation */}
            <div className='mt-6 flex items-center justify-between'>
              <button
                onClick={goToPrev}
                disabled={currentPage === 0}
                className='inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
              >
                <ChevronLeftIcon className='h-4 w-4' />
                Previous
              </button>

              {/* Page dots */}
              <div className='flex items-center gap-1.5'>
                {pages.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`h-2 rounded-full transition-all ${
                      i === currentPage
                        ? 'w-6 bg-indigo-600 dark:bg-indigo-400'
                        : 'w-2 bg-gray-300 hover:bg-gray-400 dark:bg-slate-600 dark:hover:bg-slate-500'
                    }`}
                    aria-label={`Go to ${pages[i].label}`}
                  />
                ))}
              </div>

              <button
                onClick={goToNext}
                disabled={currentPage === pages.length - 1}
                className='inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
              >
                Next
                <ChevronRightIcon className='h-4 w-4' />
              </button>
            </div>
          </div>
        </div>

        {/* Video example */}
        <div className='mx-auto mt-20 max-w-3xl text-center'>
          <span className='inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'>
            New
          </span>
          <h3 className='mt-3 text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl'>
            Watch the Story Come to Life
          </h3>
          <p className='mt-3 text-lg text-gray-600 dark:text-slate-400'>
            Every social story can now become a short, gently narrated video — a
            warm storybook animation with a calm voice reading the story aloud.
            This one was generated from David&apos;s cafeteria story.
          </p>
          <video
            controls
            preload='none'
            poster='https://media.easy-peasy.ai/landing/social-story-video-example-poster.jpg'
            src='https://media.easy-peasy.ai/landing/social-story-video-example.mp4'
            className='mt-8 aspect-video w-full rounded-2xl shadow-xl'
          />
        </div>

        {/* CTA row */}
        <div className='mt-12 flex flex-col items-center gap-4 sm:flex-row sm:justify-center'>
          <Link
            href={SOCIAL_STORIES_BOT_URL}
            className='inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
          >
            <SparklesIcon className='h-5 w-5' />
            Create Your Own Story
          </Link>
          <a
            href={SOCIAL_STORIES_PDF_URL}
            target='_blank'
            rel='noopener noreferrer'
            className='inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600'
          >
            <DownloadIcon className='h-5 w-5' />
            Download Full PDF
          </a>
        </div>
      </div>
    </section>
  );
};

export default SocialStoryExample;
